import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'

/**
 * Frankencoin savings modules — the ZCHF savings rate ("Leitzins"), on
 * every chain that runs one.
 *
 * ZCHF is a Swiss-franc stablecoin; a savings module is a plain account
 * book, not a vault: deposits stay in the module (no lending, no
 * rehypothecation) and interest is minted by the protocol at a
 * governance-set rate. `currentRatePPM()` is that rate in parts per
 * million, applied **linearly** — `calculateInterest` is
 * `Δticks × saved / 1e6 / 365 days` with ticks advancing at
 * `currentRatePPM` per second — so `ppm / 10_000` is the nominal APR in
 * percent, the package-wide convention (compounding only happens when
 * an account is `refresh`ed, which is opportunistic). 35 000 ppm =
 * 3.5 %.
 *
 * ## Why this is read PER CHAIN and not once
 *
 * Ethereum is the reference deployment; the bridged chains each run
 * their own module contract with their own stored rate, kept in sync by
 * a CCIP `ccipLeadrateSender` push. Synced is not the same as shared —
 * a push can lag or fail, and the stored value is what a depositor
 * actually earns. So each module is read on its own chain and emitted
 * under its own key, the Spark-V2 / USDD-pot posture. They all read
 * 35 000 ppm today (verified 2026-08-12 across all nine modules).
 *
 * A per-chain read also means one chain's RPC outage cannot take the
 * others down: legs are settled independently and the fetcher only
 * throws when NOTHING resolved, so `safeFetch` drops the missing keys
 * (→ `supplyRate` 0 = "unknown") rather than asserting no yield.
 *
 * **Trap — the published address config is stale on Ethereum.** The
 * `@frankencoin/zchf` package exports `savingsV2 = 0x3BF301B0…`, which
 * now runs at 1 % and holds 17k ZCHF. The live module is the
 * `savingsReferral` deployment `0x27d9AD98…` at 3.5 % holding 12.13M.
 * Verify any candidate with `currentRatePPM()` **and** the ZCHF balance
 * before trusting a config file — the retired module answers the first
 * call perfectly well. (The bridged chains have no such split: their
 * `ccipBridgedSavings` entry is the only module.)
 *
 * Rate governance is a two-step propose/apply with a `nextChange`
 * timestamp, so the rate is stable over hours, not blocks — the API
 * fallback below cannot go meaningfully stale relative to the chain.
 */

/**
 * The savings modules we register, with the balance each held at
 * 2026-08-12 (source: `/savings/core/info`, cross-checked on-chain).
 *
 * These three are 99.99 % of a 13.66M ZCHF book. Frankencoin runs four
 * more that are real and functional at the same 35 000 ppm but hold
 * essentially nothing, so they are deliberately NOT registered —
 * publishing four more identical "Frankencoin ZCHF · 3.5 %" rows at
 * $0 TVL would read as a bug, not as an option:
 *
 *   Optimism  10     `0x6426324Af1b14Df3cd03b2d500529083c5ea61BC`   1 ZCHF
 *   Polygon   137    `0xB519BAE359727e69990C27241Bef29b394A0ACbD`   0.0001
 *   Arbitrum  42161  `0xb41715e54e9f0827821A149AE8eC1aF70aa70180`   0
 *   Avalanche 43114  `0x8e7c2a697751a1cE7a8DB51f01B883A27c5c8325`   0
 *   Sonic     146    `0x4E104918908293cd6A93E1A9bbe06C345d751235`   0
 *
 * Adding one is a line here plus a line in the savings registry and in
 * the calldata-sdk withdraw registry — nothing else. The trigger is a
 * non-trivial balance; poll `/savings/core/info`, which returns every
 * module on every chain in one unauthenticated call.
 */
export interface FrankencoinSavingsDeployment {
  chainId: string
  /** The savings module contract on that chain. */
  module: Address
  /** ZCHF on that chain — canonical on 1, the CCIP token elsewhere. */
  zchf: Address
}

/** The canonical ZCHF, Ethereum. */
export const ZCHF_ETHEREUM: Address =
  '0xb58e61c3098d85632df34eecfb899a1ed80921cb'

/**
 * The CCIP-bridged ZCHF. Same address on all seven bridged chains — it
 * is a single CREATE2 deployment, so this is one constant, not seven.
 */
export const ZCHF_BRIDGED: Address =
  '0xd4dd9e2f021bb459d5a5f6c24c12fe09c5d45553'

export const FRANKENCOIN_SAVINGS_MODULES: FrankencoinSavingsDeployment[] = [
  {
    chainId: Chain.ETHEREUM_MAINNET,
    // `savingsReferral`, NOT the config's `savingsV2` — see the header.
    module: '0x27d9ad987bde08a0d083ef7e0e4043c857a17b38',
    zchf: ZCHF_ETHEREUM,
  },
  {
    chainId: Chain.GNOSIS,
    module: '0xbf594d0fed79ae56d910cb01b5dd4f4c57b04402',
    zchf: ZCHF_BRIDGED,
  },
  {
    chainId: Chain.BASE,
    module: '0x6426324af1b14df3cd03b2d500529083c5ea61bc',
    zchf: ZCHF_BRIDGED,
  },
]

/**
 * Live Ethereum savings module (`savingsReferral`).
 *
 * Kept as a named export because it is the reference deployment and the
 * one the stale-config trap applies to; prefer
 * {@link FRANKENCOIN_SAVINGS_MODULES} when you need all of them.
 */
export const FRANKENCOIN_SAVINGS_MODULE: Address =
  FRANKENCOIN_SAVINGS_MODULES[0].module

/** Per-module rate snapshot, keyed `status.<chainId>.<moduleAddr>`. */
const SAVINGS_INFO_URL = 'https://api.frankencoin.com/savings/core/info'

const RATE_ABI = [
  {
    name: 'currentRatePPM',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint24' }],
  },
] as const

/**
 * Registry `yieldKey` for the Ethereum module. Deliberately NOT the bare
 * symbol: the yielding thing is the **module**, not the ZCHF token, and a
 * `ZCHF` key would read as "holding ZCHF pays 3.5 %".
 */
export const ZCHF_SAVINGS_KEY = 'ZCHF_SAVINGS'

/**
 * Result key for one chain's module.
 *
 * Ethereum keeps the bare, already-published `ZCHF_SAVINGS` — the
 * recorder and yield-tracer consume margin-fetcher from npm, so
 * re-spelling an existing key would silently drop the rate for the
 * largest module until every consumer redeployed. New chains are
 * suffixed.
 */
export const zchfSavingsKey = (chainId: string): string =>
  chainId === Chain.ETHEREUM_MAINNET
    ? ZCHF_SAVINGS_KEY
    : `${ZCHF_SAVINGS_KEY}::${chainId}`

/**
 * The verified token-list `assetGroup` for ZCHF (identical on all eight
 * chains: `Frankencoin::ZCHF`), recorded here because it is easy to get
 * wrong — but **deliberately not emitted** as a second key, unlike
 * [frax.ts](./frax.ts) / [parallel.ts](./parallel.ts) / [sky.ts](./sky.ts).
 *
 * Every other dual-key fetcher qualifies a *yield-bearing wrapper*
 * (`Staked Frax USD::sfrxUSD`, `Staked USDp::sUSDp`, `sUSDS::SUSDS`),
 * for which the lending join `intrinsicYields[asset.assetGroup]` is
 * exactly right: that token accrues wherever it sits, including inside
 * a lending market. Frankencoin has no such wrapper on the chains we
 * register. ZCHF earns nothing by being held — the 3.5 % requires it to
 * be *inside the savings module*, which is mutually exclusive with
 * being supplied to a lender. Emitting the group would therefore credit
 * every ZCHF lending market with an intrinsic yield it does not have,
 * the same failure mode [rwaFunds.ts](./rwaFunds.ts) avoids by
 * withholding impostor groups.
 *
 * The one genuine wrapper is **`svZCHF`**, and it gets its OWN group key
 * below rather than riding on this one.
 */
export const ZCHF_GROUP_KEY = 'Frankencoin::ZCHF'

/**
 * `svZCHF` — the ERC-4626 wrapper, and the one Frankencoin token that DOES
 * accrue while it sits somewhere else.
 *
 * This is the case the note above says would justify a group key, and it was
 * left unemitted for a stale reason: at integration the Ethereum deployments
 * held ~3.5 and ~1.1 ZCHF and were written off as dust. Size was the wrong
 * test. **`0xe5f130253f…` is the collateral of a LIVE LlamaLend market**
 * (`oneway-v2-3` on Ethereum, svZCHF against crvUSD), and that market's
 * collateral row reads `intrinsicYields[assetGroup]` — so with no key emitted
 * it reported a 0 % intrinsic yield for a token earning the savings rate. The
 * market being nearly empty today does not make the wiring right; it makes
 * the bug cheap.
 *
 * BOTH chain-1 contracts carry this same group (`0xe5f130253f…`, which is in
 * Frankencoin's own address config, and `0x637f00cab9…`). That is safe rather
 * than a collision: verified on-chain, both are ERC-4626 over ZCHF holding
 * ZERO idle balance and depositing everything into the same savings module,
 * so the forward rate is identical for both. Their share prices differ
 * (1.0211 vs 1.0355) only because they were seeded at different times, and an
 * accumulated price is not what `intrinsicYield` reports.
 *
 * The value published is the ETHEREUM module's rate — svZCHF wraps that
 * module. It is a slight OVERSTATEMENT for a wrapper that deposits often,
 * since each deposit re-weights the 3-day `INTEREST_DELAY` across the whole
 * position; for a long-lived vault the drag rounds away.
 */
export const SVZCHF_GROUP_KEY = 'SavingsVault ZCHF::svZCHF'

/** ppm → nominal APR percent. 35_000 ppm = 3.5 %. */
export const aprFromRatePPM = (ppm: bigint | number): number =>
  Number(ppm) / 10_000

/** On-chain leg for ONE module — authoritative. */
const fetchRateOnChain = async (
  d: FrankencoinSavingsDeployment,
): Promise<number> => {
  const [ppm] = (await multicallRetryUniversal({
    chain: d.chainId,
    abi: RATE_ABI,
    calls: [{ address: d.module, name: 'currentRatePPM', params: [] }],
    allowFailure: false,
  })) as [bigint | number]
  if (ppm === undefined || ppm === null) {
    throw new Error(`Frankencoin: unreadable currentRatePPM() on ${d.chainId}`)
  }
  return aprFromRatePPM(ppm)
}

/**
 * API leg — unauthenticated, CORS-open, and chain-keyed, so ONE request
 * backfills every module we failed to read on-chain. Returns a map from
 * chainId to APR percent, skipping anything malformed rather than
 * throwing: a partial answer is still worth more than none.
 */
const fetchRatesFromApi = async (): Promise<Record<string, number>> => {
  const res = await fetch(SAVINGS_INFO_URL, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`Frankencoin: savings info HTTP ${res.status}`)
  const body = (await res.json()) as {
    status?: Record<string, Record<string, { rate?: number }>>
  }
  const out: Record<string, number> = {}
  for (const d of FRANKENCOIN_SAVINGS_MODULES) {
    // The API lowercases module addresses; ours are lowercased too, but
    // do not rely on it — a case flip here would silently look like a
    // dead API.
    const perChain = body?.status?.[d.chainId]
    if (!perChain) continue
    const entry = Object.entries(perChain).find(
      ([addr]) => addr.toLowerCase() === d.module.toLowerCase(),
    )?.[1]
    const rate = entry?.rate
    if (typeof rate === 'number' && Number.isFinite(rate)) {
      out[d.chainId] = aprFromRatePPM(rate)
    }
  }
  return out
}

export const frankencoinSavingsFetcher: YieldFetcher = {
  label: 'FRANKENCOIN_SAVINGS',
  fetch: async () => {
    // Two independent legs: an RPC outage must not be able to publish a
    // 0 % savings rate, and neither must a dead API. Both are issued at
    // once and reconciled per chain, so a single bad chain costs only
    // its own key.
    const [chainLegs, apiLeg] = await Promise.all([
      Promise.allSettled(FRANKENCOIN_SAVINGS_MODULES.map(fetchRateOnChain)),
      fetchRatesFromApi().catch((e) => e as Error),
    ])
    const apiRates = apiLeg instanceof Error ? {} : apiLeg

    const out: Record<string, number> = {}
    const failures: string[] = []
    FRANKENCOIN_SAVINGS_MODULES.forEach((d, i) => {
      const leg = chainLegs[i]
      if (leg.status === 'fulfilled') {
        out[zchfSavingsKey(d.chainId)] = leg.value
        return
      }
      const fromApi = apiRates[d.chainId]
      if (fromApi !== undefined) {
        out[zchfSavingsKey(d.chainId)] = fromApi
        return
      }
      failures.push(`${d.chainId}: ${String(leg.reason)}`)
    })

    // The svZCHF wrapper, keyed by its token-list `assetGroup` so the LENDING
    // join picks it up wherever it is used as collateral. Deliberately tied to
    // the ETHEREUM rate — svZCHF wraps that module — so a Gnosis-only success
    // publishes nothing here rather than attributing the wrong chain's rate.
    const ethRate = out[zchfSavingsKey(Chain.ETHEREUM_MAINNET)]
    if (ethRate !== undefined) out[SVZCHF_GROUP_KEY] = ethRate

    if (Object.keys(out).length === 0) {
      throw new Error(
        `Frankencoin: every savings module failed (${failures.join('; ')}` +
          `${apiLeg instanceof Error ? `; api: ${apiLeg.message}` : ''})`,
      )
    }
    return out
  },
}
